import { date as formatDate } from '@frameio/i18n';
import { getIsStorageAddOnIncluded } from '@frameio/plan-management/plans/utils';
import { getLink } from '@frameio/shared/routing/getLink';
import { formatBytes } from '@frameio/utils/formatters';
import { AlertBanner } from '@frameio/vapor';
import { useStorageLimitBanner } from '../hooks/useStorageLimitBanner';
import type { AlertBannerProps } from '../types';
import { UpgradeBannerWrapper } from './BannerWrapper';
import TEXT from './generated/TEXT/StorageLimitAlertBanner';
import { StyledButton } from './StyledButton';
import { SupportButton } from './SupportButton';
import { UniversalLink } from './UniversalLink';

const SETTINGS_PROJECTS_ACTIVE = getLink({
  page: 'settings',
  section: 'projects',
  filter: { status: 'active' },
});
const SETTINGS_USERS = getLink({ page: 'settings', section: 'users' });
const SETTINGS_USAGE = getLink({ page: 'settings', section: 'usage' });

TEXT.DEFINE(
  {
    /** Title for billing managers */
    titleWithPermission: (activeStorage: string, activeStorageLimit: string) =>
      `Storage Limit Exceeded (${activeStorage} of ${activeStorageLimit})`,

    /** Title for non-billing users */
    titleNoPermission: 'Storage Limit Exceeded',

    //==================== NO PERMISSION VARIATIONS (6) ====================

    /** Message for Adobe-managed Team accounts without billing permissions */
    detailsNoPermissionAdobeTeam:
      'Contact your Adobe admin to purchase more licenses—each license includes storage',

    /** Message for Adobe-managed non-Team accounts without billing permissions */
    detailsNoPermissionAdobeNotTeam: 'Contact your Adobe admin for assistance.',

    /** Message for non-billing users without grace period */
    detailsNoPermissionNoGrace: (ownerEmail: string) => (
      <>
        Contact account owner{' '}
        <UniversalLink href={`mailto:${ownerEmail}`} target="_blank">
          ({ownerEmail})
        </UniversalLink>{' '}
        for assistance.
      </>
    ),

    /** Message for non-billing users with grace period */
    detailsNoPermissionWithGrace: (ownerEmail: string, graceDate: Date) => (
      <>
        Contact account owner{' '}
        <UniversalLink href={`mailto:${ownerEmail}`} target="_blank">
          ({ownerEmail})
        </UniversalLink>{' '}
        for assistance before {formatDate(graceDate, 'medium')} or account will
        be locked.
      </>
    ),

    //==================== WITH PERMISSION: FREE/TRIAL (4) ====================

    /** Message for Free/Trial users with upgrade option and grace period */
    detailsFreeWithUpgradeWithGrace: (
      upgradedPlanTitle: string,
      openUpgradeModal: VoidFunction,
      manageStorageLink: string,
      graceDate: Date
    ) => (
      <>
        <StyledButton onClick={openUpgradeModal} variant="link">
          Subscribe to {upgradedPlanTitle}
        </StyledButton>{' '}
        or{' '}
        <UniversalLink href={manageStorageLink}>
          manage storage usage
        </UniversalLink>{' '}
        before {formatDate(graceDate, 'medium')} or account will be locked.
      </>
    ),

    /** Message for Free/Trial users with upgrade option, no grace period */
    detailsFreeWithUpgradeNoGrace: (
      upgradedPlanTitle: string,
      openUpgradeModal: VoidFunction,
      manageStorageLink: string
    ) => (
      <>
        <StyledButton onClick={openUpgradeModal} variant="link">
          Subscribe to {upgradedPlanTitle}
        </StyledButton>{' '}
        or{' '}
        <UniversalLink href={manageStorageLink}>
          manage storage usage
        </UniversalLink>
        .
      </>
    ),

    /** Message for Free/Trial users without upgrade option, with grace period */
    detailsFreeNoUpgradeWithGrace: (
      manageStorageLink: string,
      graceDate: Date
    ) => (
      <>
        <UniversalLink href={manageStorageLink}>
          Manage storage usage
        </UniversalLink>{' '}
        before {formatDate(graceDate, 'medium')} or account will be locked.
      </>
    ),

    /** Message for Free/Trial users without upgrade option, no grace period */
    detailsFreeNoUpgradeNoGrace: (manageStorageLink: string) => (
      <>
        <UniversalLink href={manageStorageLink}>
          Manage storage usage
        </UniversalLink>
        .
      </>
    ),

    //==================== WITH PERMISSION: ADOBE + TEAM (1) ====================

    /** Message for Adobe-managed Team billing managers */
    detailsAdobeTeamWithPermission: (manageStorageLink: string) => (
      <>
        <UniversalLink href={manageStorageLink}>
          Manage storage usage
        </UniversalLink>{' '}
        or contact your Adobe admin to purchase more licenses—each license
        includes storage.
      </>
    ),

    //==================== WITH PERMISSION: ENTERPRISE (2) ====================

    /** Message for Enterprise billing managers with grace period */
    detailsEnterpriseWithGrace: (
      manageStorageLink: string,
      graceDate: Date
    ) => (
      <>
        <UniversalLink href={manageStorageLink}>
          Manage storage usage
        </UniversalLink>{' '}
        or <SupportButton>contact support</SupportButton> for assistance before{' '}
        {formatDate(graceDate, 'medium')} or account will be locked.
      </>
    ),

    /** Message for Enterprise billing managers, no grace period */
    detailsEnterpriseNoGrace: (manageStorageLink: string) => (
      <>
        <UniversalLink href={manageStorageLink}>
          Manage storage usage
        </UniversalLink>{' '}
        or <SupportButton>contact support</SupportButton> for assistance.
      </>
    ),

    //==================== WITH PERMISSION: PAID (PERSTORAGEINCREMENT > 0) (4) ====================

    /** Message for paid plan billing managers with per-user storage, upgrade option, and grace period */
    detailsPaidPerUserWithUpgradeWithGrace: (
      perUserStorage: string,
      upgradedPlanTitle: string,
      openUpgradeModal: VoidFunction,
      manageStorageLink: string,
      graceDate: Date
    ) => (
      <>
        <UniversalLink href={SETTINGS_USERS}>Add new users</UniversalLink> (+
        {perUserStorage} each),{' '}
        <StyledButton onClick={openUpgradeModal} variant="link">
          subscribe to {upgradedPlanTitle}
        </StyledButton>
        , or{' '}
        <UniversalLink href={manageStorageLink}>manage storage</UniversalLink>{' '}
        before {formatDate(graceDate, 'medium')} or account will be locked.
      </>
    ),

    /** Message for paid plan billing managers with per-user storage, upgrade option, no grace period */
    detailsPaidPerUserWithUpgradeNoGrace: (
      perUserStorage: string,
      upgradedPlanTitle: string,
      openUpgradeModal: VoidFunction,
      manageStorageLink: string
    ) => (
      <>
        <UniversalLink href={SETTINGS_USERS}>Add new users</UniversalLink> (+
        {perUserStorage} each),{' '}
        <StyledButton onClick={openUpgradeModal} variant="link">
          subscribe to {upgradedPlanTitle}
        </StyledButton>
        , or{' '}
        <UniversalLink href={manageStorageLink}>manage storage</UniversalLink>.
      </>
    ),

    /** Message for paid plan billing managers with per-user storage, no upgrade, with grace period */
    detailsPaidPerUserNoUpgradeWithGrace: (
      perUserStorage: string,
      manageStorageLink: string,
      graceDate: Date
    ) => (
      <>
        <UniversalLink href={SETTINGS_USERS}>Add new users</UniversalLink> (+
        {perUserStorage} each), or{' '}
        <UniversalLink href={manageStorageLink}>manage storage</UniversalLink>{' '}
        before {formatDate(graceDate, 'medium')} or account will be locked.
      </>
    ),

    /** Message for paid plan billing managers with per-user storage, no upgrade, no grace period */
    detailsPaidPerUserNoUpgradeNoGrace: (
      perUserStorage: string,
      manageStorageLink: string
    ) => (
      <>
        <UniversalLink href={SETTINGS_USERS}>Add new users</UniversalLink> (+
        {perUserStorage} each), or{' '}
        <UniversalLink href={manageStorageLink}>manage storage</UniversalLink>.
      </>
    ),

    //==================== WITH PERMISSION: PAID (PERSTORAGEINCREMENT = 0) (4) ====================

    /** Message for paid plan billing managers without per-user storage, upgrade option, with grace period */
    detailsPaidNoPerUserWithUpgradeWithGrace: (
      upgradedPlanTitle: string,
      openUpgradeModal: VoidFunction,
      manageStorageLink: string,
      graceDate: Date
    ) => (
      <>
        <StyledButton onClick={openUpgradeModal} variant="link">
          Subscribe to {upgradedPlanTitle}
        </StyledButton>
        , or{' '}
        <UniversalLink href={manageStorageLink}>manage storage</UniversalLink>{' '}
        before {formatDate(graceDate, 'medium')} or account will be locked.
      </>
    ),

    /** Message for paid plan billing managers without per-user storage, upgrade option, no grace period */
    detailsPaidNoPerUserWithUpgradeNoGrace: (
      upgradedPlanTitle: string,
      openUpgradeModal: VoidFunction,
      manageStorageLink: string
    ) => (
      <>
        <StyledButton onClick={openUpgradeModal} variant="link">
          Subscribe to {upgradedPlanTitle}
        </StyledButton>
        , or{' '}
        <UniversalLink href={manageStorageLink}>manage storage</UniversalLink>.
      </>
    ),

    /** Message for paid plan billing managers without per-user storage, no upgrade, with grace period */
    detailsPaidNoPerUserNoUpgradeWithGrace: (
      manageStorageLink: string,
      graceDate: Date
    ) => (
      <>
        <UniversalLink href={manageStorageLink}>Manage storage</UniversalLink>{' '}
        before {formatDate(graceDate, 'medium')} or account will be locked.
      </>
    ),

    /** Message for paid plan billing managers without per-user storage, no upgrade, no grace period */
    detailsPaidNoPerUserNoUpgradeNoGrace: (manageStorageLink: string) => (
      <>
        <UniversalLink href={manageStorageLink}>Manage storage</UniversalLink>.
      </>
    ),
  },
  { StyledButton, SupportButton, SETTINGS_USERS, UniversalLink }
);

const getDetails = (
  canManageBilling: boolean,
  graceDate: Date | null,
  hasStorageAddOn: boolean,
  isEnterprise: boolean,
  isFree: boolean,
  isManagedByAdobe: boolean,
  isTeam: boolean,
  isTrialActive: boolean,
  manageStorageLink: string,
  openUpgradeModal: VoidFunction,
  ownerEmail: string,
  perUserStorageIncrement: number,
  subscriptionEndAt: string | null | undefined,
  upgradedPlanTitle: string | undefined
) => {
  const isFreeOrTrialActive = isFree || isTrialActive;
  const canUpgrade = !!upgradedPlanTitle && upgradedPlanTitle !== 'Enterprise';
  const hasGracePeriod = !!graceDate;

  // NO PERMISSION PATH
  if (!canManageBilling) {
    if (isManagedByAdobe) {
      return isTeam
        ? TEXT.detailsNoPermissionAdobeTeam
        : TEXT.detailsNoPermissionAdobeNotTeam;
    }
    if (isEnterprise || !hasGracePeriod) {
      return TEXT.detailsNoPermissionNoGrace(ownerEmail);
    }
    return TEXT.detailsNoPermissionWithGrace(ownerEmail, graceDate);
  }

  // WITH PERMISSION: FREE/TRIAL
  if (isFreeOrTrialActive) {
    if (canUpgrade) {
      if (hasGracePeriod) {
        return TEXT.detailsFreeWithUpgradeWithGrace(
          upgradedPlanTitle,
          openUpgradeModal,
          manageStorageLink,
          graceDate
        );
      }
      return TEXT.detailsFreeWithUpgradeNoGrace(
        upgradedPlanTitle,
        openUpgradeModal,
        manageStorageLink
      );
    }
    if (hasGracePeriod) {
      return TEXT.detailsFreeNoUpgradeWithGrace(manageStorageLink, graceDate);
    }
    return TEXT.detailsFreeNoUpgradeNoGrace(manageStorageLink);
  }

  // WITH PERMISSION: ADOBE + TEAM
  if (isManagedByAdobe && isTeam) {
    return TEXT.detailsAdobeTeamWithPermission(manageStorageLink);
  }

  // WITH PERMISSION: ENTERPRISE
  if (isEnterprise) {
    if (hasGracePeriod) {
      return TEXT.detailsEnterpriseWithGrace(manageStorageLink, graceDate);
    }
    return TEXT.detailsEnterpriseNoGrace(manageStorageLink);
  }

  // WITH PERMISSION: PAID (perUserStorageIncrement > 0)
  if (perUserStorageIncrement > 0) {
    const perUserStorage = formatBytes(perUserStorageIncrement);

    if (canUpgrade) {
      if (hasGracePeriod) {
        return TEXT.detailsPaidPerUserWithUpgradeWithGrace(
          perUserStorage,
          upgradedPlanTitle,
          openUpgradeModal,
          manageStorageLink,
          graceDate
        );
      }
      return TEXT.detailsPaidPerUserWithUpgradeNoGrace(
        perUserStorage,
        upgradedPlanTitle,
        openUpgradeModal,
        manageStorageLink
      );
    }
    if (hasGracePeriod) {
      return TEXT.detailsPaidPerUserNoUpgradeWithGrace(
        perUserStorage,
        manageStorageLink,
        graceDate
      );
    }
    return TEXT.detailsPaidPerUserNoUpgradeNoGrace(
      perUserStorage,
      manageStorageLink
    );
  }

  // WITH PERMISSION: PAID (perUserStorageIncrement = 0)
  if (canUpgrade) {
    if (hasGracePeriod) {
      return TEXT.detailsPaidNoPerUserWithUpgradeWithGrace(
        upgradedPlanTitle,
        openUpgradeModal,
        manageStorageLink,
        graceDate
      );
    }
    return TEXT.detailsPaidNoPerUserWithUpgradeNoGrace(
      upgradedPlanTitle,
      openUpgradeModal,
      manageStorageLink
    );
  }
  if (hasGracePeriod) {
    return TEXT.detailsPaidNoPerUserNoUpgradeWithGrace(
      manageStorageLink,
      graceDate
    );
  }
  return TEXT.detailsPaidNoPerUserNoUpgradeNoGrace(manageStorageLink);
};

export function StorageLimitAlertBanner({
  account,
  fetchPlansData,
  upgradedPlan,
}: AlertBannerProps) {
  const {
    canManageBilling,
    showBanner,
    subscription,
    tier,
    isEnterprise,
    isTeam,
    activeStorage,
    activeStorageLimit,
    ownerEmail,
    isManagedByAdobe,
    graceDate,
    upgradedPlanTitle,
    openUpgradeModal,
  } = useStorageLimitBanner(account, fetchPlansData, upgradedPlan);

  const isFree = tier === 'FREE';
  const isTrialActive = subscription?.trial?.isActive;
  const perUserStorageIncrement =
    subscription?.plan?.perUserStorageIncrement ?? 0;
  const hasStorageAddOn = getIsStorageAddOnIncluded(subscription?.addOns);

  if (!showBanner) return null;

  const canManageStorage =
    !isFree && !isTrialActive && Boolean(canManageBilling);
  const manageStorageLink =
    canManageStorage &&
    !isEnterprise &&
    hasStorageAddOn &&
    !subscription?.subscriptionEndAt
      ? SETTINGS_USAGE
      : SETTINGS_PROJECTS_ACTIVE;

  const title = canManageBilling
    ? TEXT.titleWithPermission(
        formatBytes(activeStorage ?? 0),
        formatBytes(activeStorageLimit ?? 0)
      )
    : TEXT.titleNoPermission;

  const details = getDetails(
    Boolean(canManageBilling),
    graceDate,
    hasStorageAddOn,
    isEnterprise,
    isFree,
    isManagedByAdobe,
    isTeam,
    isTrialActive ?? false,
    manageStorageLink,
    openUpgradeModal,
    ownerEmail,
    perUserStorageIncrement,
    subscription?.subscriptionEndAt,
    upgradedPlanTitle
  );

  return (
    <UpgradeBannerWrapper onHoverIntent={fetchPlansData}>
      <AlertBanner details={details} title={title} variant="notice" />
    </UpgradeBannerWrapper>
  );
}
